import React, { useState } from 'react';
import { getProvider } from '../utils/videoProviders';
import { API_BASE_URL } from '../config';

const isNativeApp = () =>
  typeof window !== 'undefined' &&
  (window.Capacitor || /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

function getEmbedOrigin() {
  if (typeof window === 'undefined') return '';
  const base = (API_BASE_URL || '').replace(/\/$/, '');
  return base || window.location.origin || '';
}

/**
 * Single embed for any supported video provider (YouTube, Vimeo, TikTok, Dailymotion).
 * Same look: thumbnail + play button, then iframe on click. YouTube uses native plugin on iOS when available.
 */
const VideoEmbed = ({ provider: providerConfig, videoId }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const provider = providerConfig && getProvider(providerConfig.id) ? getProvider(providerConfig.id) : providerConfig;
  if (!provider || !videoId) return null;

  const thumbnailUrl = provider.getThumbnailUrl ? provider.getThumbnailUrl(videoId) : null;
  const playButtonClass = provider.playButtonClass || 'bg-red-600';
  const useNativeFlow =
    provider.id === 'youtube' &&
    isNativeApp() &&
    (API_BASE_URL || '').replace(/\/$/, '');
  const origin = getEmbedOrigin();

  // YouTube on native: use Capacitor plugin
  if (useNativeFlow) {
    const openNativePlayer = async () => {
      const Plugins = window.Capacitor?.Plugins;
      const plugin = Plugins?.YouTubePlayer ?? Plugins?.YouTubePlayerPlugin;
      if (!plugin?.open) {
        setIsPlaying(true);
        return;
      }
      try {
        await plugin.open({
          videoId,
          origin: origin || API_BASE_URL,
          returnUrl: window.location.origin || 'capacitor://localhost',
        });
      } catch (e) {
        console.warn('YouTubePlayer.open failed', e);
        setIsPlaying(true);
      }
    };
    return (
      <div
        className="mb-2 rounded-lg overflow-hidden bg-gray-900 cursor-pointer group relative"
        style={{ paddingBottom: '56.25%' }}
        onClick={openNativePlayer}
      >
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={`${provider.name} video`}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
        )}
        {!thumbnailUrl && (
          <div className="absolute inset-0 bg-gray-800" />
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-10 transition-all">
          <div className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 ${playButtonClass} rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl`}>
            <i className="fas fa-play text-white text-lg sm:text-xl md:text-2xl lg:text-3xl ml-0.5 sm:ml-1" />
          </div>
        </div>
      </div>
    );
  }

  // Playing: show iframe
  if (isPlaying) {
    let embedSrc = provider.getEmbedUrl(videoId);
    if (provider.id === 'youtube') {
      const base = (API_BASE_URL || '').replace(/\/$/, '');
      const params = `autoplay=1&rel=0&playsinline=1${origin ? `&origin=${encodeURIComponent(origin)}` : ''}`;
      embedSrc = base ? `${base}/embed/youtube/${videoId}?${params}` : `${embedSrc}?${params}`;
    }
    return (
      <div className="mb-2 rounded-lg overflow-hidden bg-black">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={embedSrc}
            className="absolute top-0 left-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            title={`${provider.name} video player`}
          />
        </div>
      </div>
    );
  }

  // Preview: thumbnail + play overlay (or low-res iframe for YouTube)
  const youtubePreviewSrc =
    provider.id === 'youtube'
      ? (() => {
          const base = (API_BASE_URL || '').replace(/\/$/, '');
          const params = `start=0&rel=0&modestbranding=1&playsinline=1${origin ? `&origin=${encodeURIComponent(origin)}` : ''}`;
          const ytBase = provider.getEmbedUrl(videoId);
          return base ? `${base}/embed/youtube/${videoId}?${params}` : `${ytBase}?${params}`;
        })()
      : null;

  return (
    <div
      className="mb-2 rounded-lg overflow-hidden bg-gray-900 cursor-pointer group relative"
      onClick={() => setIsPlaying(true)}
    >
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        {provider.id === 'youtube' && youtubePreviewSrc ? (
          <iframe
            src={youtubePreviewSrc}
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            frameBorder="0"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            title={`${provider.name} preview`}
            style={{ opacity: 0.95 }}
          />
        ) : thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={`${provider.name} video`}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
            <span className="text-gray-500 text-sm">{provider.name}</span>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-10 transition-all pointer-events-none">
          <div className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 ${playButtonClass} rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl`}>
            <i className="fas fa-play text-white text-lg sm:text-xl md:text-2xl lg:text-3xl ml-0.5 sm:ml-1" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoEmbed;
